<script setup lang="ts">
/**
 * 问题抽屉里的辅助操作：5Why 追问、根因写成 D4、历史案例草稿、一键铺 D0–D8。
 *
 * 这一整块原来只做在模块中心，项目页没有——而人实际是在项目里看问题的
 * （2026-08-18 审计：8D 一键铺、5Why/AI 都是「模块中心有、项目页没有」）。
 * 抽成组件挂进 RecordDrawer 的 #assist，两边就自动一致了。
 *
 * 表单值不由这里持有：抽屉的活表单在 RecordFormDialog 里，
 * 用 getValues / setValues 两个回调读写——AI 填进来的内容只填不存，
 * 人确认后点「保存修改」才落库。
 */
import { computed, nextTick, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import DelegateDialog from '@/components/DelegateDialog.vue';
import IssueSolutionAssist from '@/components/IssueSolutionAssist.vue';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';

const props = defineProps<{
  record: ModuleRecord;
  /** 只读账号不给写入类按钮 */
  writable?: boolean;
  getValues: () => Record<string, string>;
  setValues: (patch: Record<string, string>) => void;
}>();

const emit = defineEmits<{
  /** 写了 8D 步骤，外面的子条目列表该刷新了 */
  'steps-changed': [];
}>();

const fiveWhyBusy = ref(false);
const d4Busy = ref(false);
const starting8d = ref(false);
// 从问题发起派活（0903）：自动挂上这条问题和它的项目，跟踪表里看得见挂在哪件事上
const delegating = ref(false);
const delegationProjectId = computed(() => {
  const raw = props.record.projectId ?? props.record.project_id;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
});
const delegationReference = computed(() => ({
  type: 'issue',
  id: props.record.id,
  label: `问题 #${props.record.id}${props.record.title ? ` ${String(props.record.title).slice(0, 40)}` : ''}`,
}));

/* 历史案例那块要读七八个字段，每次渲染都调一遍 getValues 太浪费；
   读的是对话框里的活表单，依赖照样能被追踪到 */
const values = computed(() => props.getValues());

/**
 * AI 追问 5Why：读表单里的描述和已填的为什么，只把空位补上。
 * 已填的内容原样保留——AI 不改写人写的分析。
 */
async function runFiveWhyAssist(): Promise<void> {
  const values = props.getValues();
  fiveWhyBusy.value = true;
  try {
    const result = await eqsApi.fiveWhyAssist({
      title: values.title ?? '',
      description: values.description ?? '',
      whys: [1, 2, 3, 4, 5].map((n) => values[`why${n}`] ?? ''),
      rootCause: values.rootCause ?? '',
    });
    const patch: Record<string, string> = {};
    result.whys.forEach((why, index) => {
      if (!values[`why${index + 1}`]?.trim() && why) patch[`why${index + 1}`] = why;
    });
    if (!values.rootCause?.trim() && result.rootCause) patch.rootCause = result.rootCause;
    if (!Object.keys(patch).length) {
      notify('success', '5Why 已完整', 'AI 没有需要补的空位。');
      return;
    }
    props.setValues(patch);
    notify('success', 'AI 已补全 5Why 建议', '补进来的内容还没保存，确认无误后点「保存修改」；不对就直接改掉。');
  } catch (caught) {
    notify('error', 'AI 追问失败', errorMessage(caught));
  } finally {
    fiveWhyBusy.value = false;
  }
}

/**
 * 把根因结论写成 D4 步骤。
 *
 * 5Why 本来就是 D4「根本原因分析」的标准方法，但两处各填各的：
 * 人在 5Why 里推完根因，还要手工把同一段话再抄进 8D 的 D4（试用反馈 8/14）。
 * 这里一键搬过去，8D 的进度条随即亮起 D4。
 */
async function createD4FromRootCause(): Promise<void> {
  const rootCause = String(props.getValues().rootCause ?? '').trim();
  if (!rootCause) {
    notify('error', '还没有根因结论', '先填「问题原因分析（根因结论）」，或点上面的「AI 追问 5Why」让它给一版。');
    return;
  }
  d4Busy.value = true;
  try {
    const existing = await eqsApi.childRecords('issues', 'steps', props.record.id);
    // 同一 D 步内接着排号：已有 d4:1 就写 d4:2，不覆盖别人写的
    const used = existing.items
      .map((item) => String(item.stepCode ?? item.step_code ?? '').replace(/^review:/, ''))
      .filter((code) => code.split(':')[0] === 'd4')
      .map((code) => Number(code.split(':')[1] ?? '1') || 1);
    await eqsApi.createModuleRecord('issues', 'steps', {
      parentId: Number(props.record.id),
      title: rootCause,
      status: 'pending',
      stepCode: `d4:${Math.max(0, ...used) + 1}`,
    });
    notify('success', '已写入 D4 根本原因分析', '在下面的「8D 步骤」里可以继续补责任人和期限。');
    await nextTick();
    emit('steps-changed');
  } catch (caught) {
    notify('error', '写入 D4 失败', errorMessage(caught));
  } finally {
    d4Busy.value = false;
  }
}

/** 一键铺 D0–D8：后端幂等，已有步骤的不会被覆盖 */
async function start8dHere(): Promise<void> {
  if (starting8d.value) return;
  starting8d.value = true;
  try {
    const result = await eqsApi.startIssue8d(props.record.id as number);
    notify('success', result.alreadyStarted ? '这条已经有 8D' : '8D 已建好',
      result.alreadyStarted
        ? '没有重复创建，下面就是现有的步骤。'
        : `D0–D8 共 ${result.created} 步已经铺好，在下面逐步填写即可。`);
    await nextTick();
    emit('steps-changed');
  } catch (caught) {
    notify('error', '建立 8D 失败', errorMessage(caught));
  } finally {
    starting8d.value = false;
  }
}

/** 历史案例给的草稿填进表单——和 5Why 一样只填不存 */
function applySolutionDraft(patch: Record<string, string>): void {
  props.setValues(patch);
}
</script>

<template>
  <section class="status-actions">
    <span>5WHY / AI</span><h3>AI 追问 5Why</h3>
    <p class="operation-hint">沿着已填的「为什么」继续往下追到根因层，只补空位、不改已填的内容。</p>
    <div class="button-row">
      <button class="button button-secondary" type="button" :disabled="fiveWhyBusy" @click="runFiveWhyAssist">
        {{ fiveWhyBusy ? 'AI 分析中…' : 'AI 追问 5Why' }}
      </button>
      <!-- 5Why 就是 D4 的标准方法，别让人把同一段话再抄一遍 -->
      <button
        v-if="writable"
        class="button button-secondary"
        type="button"
        :disabled="d4Busy"
        @click="createD4FromRootCause"
      >{{ d4Busy ? '写入中…' : '根因写成 8D 的 D4' }}</button>
    </div>

    <!-- 参考历史案例：47 条根因措施都写清楚了的老问题，挑相似的给草稿 -->
    <IssueSolutionAssist
      :issue-id="record.id"
      :title="String(values.title ?? record.title ?? '')"
      :description="String(values.description ?? '')"
      :part-name="String(values.partName ?? '')"
      :product-category="String(values.productCategory ?? '')"
      :category="String(values.category ?? '')"
      :current-root-cause="String(values.rootCause ?? '')"
      :current-countermeasure="String(values.countermeasure ?? '')"
      @apply="applySolutionDraft"
    />
  </section>

  <!--
    8D 就在这条问题的抽屉里往下展开，不再是另一个页面。
    原来「转 8D」在清单上、填写在 8D 视图里，一件事被切成两处，
    从抽屉进来的人绕不回去（2026-08-18 Tony：「整个流程还不是很通」）。
    现在空的时候给一键铺好 D0–D8 的按钮，铺完就在下面直接填。
  -->
  <div v-if="writable" class="issue-8d-start">
    <button type="button" class="inline-button" :disabled="starting8d" @click="start8dHere">
      {{ starting8d ? '建立中…' : '一键建好 D0–D8' }}
    </button>
    <small>需要走八步法的问题点这里；不需要 8D 的问题留空即可，不影响它正常流转。</small>
  </div>

  <!-- 派活（0903）：问题里要人去办的事直接派出去，挂在这条问题上，任务跟踪表里看得见 -->
  <div class="issue-delegate">
    <button type="button" class="inline-button" @click="delegating = true">派活</button>
    <small>把这条问题里要办的事派给具体的人，有主、有期限，任务跟踪表里能追踪。</small>
  </div>
  <DelegateDialog
    v-if="delegating"
    :project-id="delegationProjectId"
    :reference="delegationReference"
    @close="delegating = false"
  />
</template>

<style scoped>
.issue-8d-start { display: flex; align-items: center; gap: 10px; margin: 14px 0 6px; }
.issue-8d-start small { color: var(--muted); font-size: 12px; }
.issue-delegate { display: flex; align-items: center; gap: 10px; margin: 6px 0; }
.issue-delegate small { color: var(--muted); font-size: 12px; }
</style>
